import { Links, Meta, Outlet, Scripts, ScrollRestoration } from "react-router";
import type { ReactNode } from "react";
import type { Route } from "./+types/root";

const TITLE = "Gnothi — Write the book of your life";
const DESCRIPTION = "Gnothi turns your journal into a story — write entries, track what matters, and let AI author the book of your life, one chapter at a time.";

// Site-wide title/OG defaults — MUST be meta() not literal <head> tags: RR replaces the whole
// descriptor array with the leaf's meta(), so series*.tsx override cleanly; literals here would
// double-render title/og on the SEO pages. og:url stays per-route (series*.tsx) — one value misattributes.
export const meta: Route.MetaFunction = () => [
  { title: TITLE },
  { name: "description", content: DESCRIPTION },
  { property: "og:title", content: TITLE },
  { property: "og:description", content: DESCRIPTION },
  { property: "og:image", content: "/og-image.jpg" },
  { property: "og:image:width", content: "1200" },
  { property: "og:image:height", content: "630" },
  { property: "og:type", content: "website" },
];

// Framework mode owns the HTML document now (replaces index.html) — favicons/fonts/boot-scripts
// stay literal here; title/og live in meta() above so leaf routes can override them.
export function Layout({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="UTF-8" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32.png" />
        <link rel="icon" type="image/x-icon" href="/favicon.ico" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="theme-color" content="#0A1128" />
        <meta name="fo-verify" content="247b505d-1f32-4c3d-8167-faf05a1e589e" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Outfit:wght@300;400;500;600;700;800&display=swap"
          rel="stylesheet"
        />
        <link rel="stylesheet" href="https://fonts.googleapis.com/icon?family=Material+Icons" />
        {/* Amplify bug https://github.com/aws-amplify/amplify-js/issues/678 */}
        <script dangerouslySetInnerHTML={{ __html: "var global = global || window;" }} />
        {/* Apply MUI's persisted dark scheme before paint (matches colorSchemeSelector:'class'
            + modeStorageKey:'mui-mode' in Setup/Mui.tsx) so refreshing in dark doesn't flash light. */}
        <script
          dangerouslySetInnerHTML={{
            __html:
              "try { if (localStorage.getItem('mui-mode') === 'dark') document.documentElement.classList.add('dark'); } catch (e) {}",
          }}
        />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return <Outlet />;
}
